import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { staffService } from '../../services/apiService';

const StaffEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    department: '',
    phoneNumber: '',
    isActive: true
  });
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const roles = [
    { value: 'LIBRARY_STAFF', label: 'Library' },
    { value: 'HOD', label: 'Head of Department (HOD)' },
    { value: 'ACCOUNTS_SECTION', label: 'Accounts Section' },
    { value: 'EXAM_SECTION', label: 'Exam Section' },
    { value: 'ADMIN', label: 'Admin' },
    { value: 'FACULTY', label: 'Faculty' },
    { value: 'STAFF', label: 'Staff' }
  ];

  const departments = [
    { value: 'CS', label: 'CS' },
    { value: 'CD', label: 'CD' },
    { value: 'CI', label: 'CI' },
    { value: 'CY', label: 'CY' },
    { value: 'EC', label: 'EC' },
    { value: 'ME', label: 'ME' }
  ];

  const isDepartmentRequired = formData.role === 'HOD' || formData.role === 'FACULTY';

  useEffect(() => {
    fetchStaffDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchStaffDetails = async () => {
    try {
      setLoading(true);
      console.log('📥 Fetching staff details for ID:', id);
      const response = await staffService.getStaffById(id);
      console.log('✅ Full API response:', response);
      
      // Backend returns { staff: {...} }
      const data = response.staff || response;
      console.log('✅ Staff data extracted:', data);
      
      setStaff(data);
      
      const populatedFormData = {
        name: data.name || '',
        role: data.role || '',
        department: data.department || '',
        phoneNumber: data.phoneNumber || '',
        isActive: data.isActive !== undefined ? data.isActive : true
      };

      if ((populatedFormData.role === 'HOD' || populatedFormData.role === 'FACULTY') && !populatedFormData.department) {
        populatedFormData.department = departments[0]?.value || '';
      }
      
      console.log('📝 Populating form with:', populatedFormData);
      setFormData(populatedFormData);
    } catch (error) {
      console.error('❌ Failed to fetch staff details:', error);
      toast.error('Failed to fetch staff details');
      navigate('/admin/staff');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate department for HOD and FACULTY
    if ((formData.role === 'HOD' || formData.role === 'FACULTY') && !formData.department) {
      toast.error(`Department is required for ${formData.role === 'HOD' ? 'Head of Department' : 'Faculty'} role`);
      return;
    }

    setSaving(true);

    try {
      await staffService.updateStaff(id, {
        name: formData.name,
        role: formData.role,
        department: formData.department || null,
        phoneNumber: formData.phoneNumber || null,
        isActive: formData.isActive
      });

      toast.success('Staff member updated successfully!');
      navigate('/admin/staff');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        errors.forEach(err => toast.error(err.msg || err.message));
      } else {
        toast.error(error.response?.data?.error || 'Failed to update staff member');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      await staffService.resetPassword(id, newPassword);
      toast.success('Password reset successfully!');
      setShowResetPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    const nextFormData = { ...formData, [name]: nextValue };

    if (name === 'role') {
      const nextRequiresDept = value === 'HOD' || value === 'FACULTY';
      if (nextRequiresDept && !nextFormData.department) {
        nextFormData.department = departments[0]?.value || '';
      }
    }

    setFormData(nextFormData);
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="text-center">
          <div className="spinner border-4 border-primary border-t-transparent w-12 h-12 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading staff details...</p>
        </div>
      </div>
    );
  }

  if (!staff) {
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-slate-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/staff')}
            className="mb-4 text-slate-600 hover:text-slate-900 flex items-center gap-2"
          >
            ← Back to Staff Management
          </button>
          <h1 className="text-3xl font-bold text-slate-900">Edit Staff Member</h1>
          <p className="text-slate-600 mt-2">Update staff account information and permissions</p>
        </div>

        <div className="space-y-6">
          {/* Main Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email (Read-Only) */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  College Email
                </label>
                <input
                  type="text"
                  value={staff.collegeEmail}
                  disabled
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-slate-100 text-slate-600"
                />
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Role *
                </label>
                <select
                  name="role"
                  required
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Department {isDepartmentRequired && <span className="text-red-500">*</span>}
                </label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  required={isDepartmentRequired}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {!isDepartmentRequired && <option value="">GLOBAL (No Limit)</option>}
                  {departments.map((dept) => (
                    <option key={dept.value} value={dept.value}>
                      {dept.label}
                    </option>
                  ))}
                </select>
                {isDepartmentRequired && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ Department is required for {formData.role === 'HOD' ? 'Head of Department' : 'Faculty'} role
                  </p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="w-5 h-5 text-primary focus:ring-2 focus:ring-primary rounded"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-slate-900 cursor-pointer">
                  Active Account
                </label>
                <p className="text-xs text-slate-500 ml-auto">
                  {formData.isActive ? 'Can log in and access system' : 'Login disabled'}
                </p>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/admin/staff')}
                  disabled={saving}
                  className="flex-1 py-3 px-6 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-3 px-6 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="spinner border-2 border-white border-t-transparent w-5 h-5"></div>
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Password Reset Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Password Management</h3>
            
            {!showResetPassword ? (
              <div>
                <p className="text-slate-600 mb-4">Reset the staff member's password if they've forgotten it or for security purposes.</p>
                <button
                  onClick={() => setShowResetPassword(true)}
                  className="px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
                >
                  Reset Password
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-orange-800">
                    ⚠️ The staff member will need to use this new password to log in. Make sure to communicate it to them securely.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      {showNewPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter new password"
                      className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      setShowResetPassword(false);
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    className="flex-1 py-3 px-6 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetPassword}
                    className="flex-1 py-3 px-6 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
                  >
                    Confirm Reset
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Account Info */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Account Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-600">Created At</p>
                <p className="font-medium text-slate-900">
                  {new Date(staff.createdAt).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div>
                <p className="text-slate-600">Last Updated</p>
                <p className="font-medium text-slate-900">
                  {new Date(staff.updatedAt).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffEdit;
